import type { ReactNode } from 'react'
import cn from 'classnames'
import { track } from '@mc/track-event'
import { TrackingContainer } from '@mc/visibility'
import Logo from 'components/Navigation/MCLogo/assets/logo.svg'
import styles from './IncludedBanner.module.scss'

const trackBannerViewed = () => {
  track('Included with MasterClass Banner Viewed')
}

export interface CMIncludedWithMasterClassBannerProps {
  text: string
  wrapper?: WrapperComponent
}

type WrapperComponent = (props: { children: ReactNode }) => JSX.Element

/**
 * Banner component with a centered layout with the MasterClass logo and customizable text.
 * Automatically tracks when the banner becomes visible in the viewport for analytics purposes.
 *
 * @param text - The promotional text to display next to the MasterClass logo
 * @param wrapper - Optional wrapper elements that will wrap the banner content block. Defaults to a centered
 * row/column layout.
 */
export const CMIncludedWithMasterClassBanner = ({
  text,
  wrapper,
}: CMIncludedWithMasterClassBannerProps) => {
  const defaultWrapper: WrapperComponent = ({ children }) => (
    <div className='row justify-content-center mc-mt-5'>
      <div
        className={cn(
          // Breakpoints carefully selected to be the same width as the CTA button whenever possible
          // (else becoming big enough to fit all text copies):
          'col-sm-9 col-md-12 col-lg-10 col-xl-8',
          styles.col6From1450,
        )}
      >
        {children}
      </div>
    </div>
  )

  const Wrapper = wrapper ?? defaultWrapper

  return (
    <TrackingContainer onFirstVisible={trackBannerViewed}>
      <Wrapper>
        <div className='mc-bg-container-dark mc-corners--sm mc-p-4'>
          <div className='d-flex align-items-center justify-content-center'>
            <Logo
              className='mc-text-color--primary mc-mr-3 flex-shrink-0'
              width={27}
            />
            <span className={cn('mc-text--bold', styles.textContent)}>
              {text}
            </span>
          </div>
        </div>
      </Wrapper>
    </TrackingContainer>
  )
}
